// ═══════════════════════════════════════════════════════════════════════════
// interfaceIntelligenceTab.jsx — The "Interface Intelligence" stakeholder tab
// ═══════════════════════════════════════════════════════════════════════════
// Surfaces the new `enhanced` layer of IFACES_ENHANCED for all 56 interfaces:
//   · criticalityScore  · moscow         · reusability
//   · systemOfRecord    · idempotency    · errorHandlingStrategy
//   · securityCompliance · monitoringSignals
//   · upstream/downstream dependency map
//   · harmoniousFlowChannel (intra vs inter system)
//   · classificationSummary (OOTB / OOTB+Config / Customization breakdown)
//
// Views:
//   1. 🌟 Top Criticality      — bubble chart, top 15 by score
//   2. 🎯 MoSCoW Matrix         — Must/Should/Could rollup
//   3. 🔄 Dependency Graph      — upstream → interface → downstream
//   4. 🌐 Outcome Map           — what business outcome each interface delivers
//   5. 🔍 Deep card grid        — every interface as a stunning card
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { T, FONTS, chipBtn, pill } from "./theme";
import {
  IFACES, GRP_META, MVP_META, PHASE_META,
  getInterfacesByCriticality, getInterfacesByMoscow,
  getAllOutcomeCategories, getInterfacesByOutcomeCategory,
} from "./IfacesData";
import { useNavigation, EntityRef } from "./navigation";

const VIEWS = [
  { id: "criticality", label: "🌟 Criticality",     color: T.cyan },
  { id: "moscow",      label: "🎯 MoSCoW matrix",   color: T.pink },
  { id: "deps",        label: "🔄 Dependency graph",color: T.amber },
  { id: "outcome",     label: "🌐 Outcome map",     color: T.green },
  { id: "cards",       label: "🔍 Card grid",       color: T.purple },
];

const MOSCOW_META = {
  "Must Have":   { color: T.red,    icon: "🚨", priority: 1, description: "Go-live blocker" },
  "Should Have": { color: T.amber,  icon: "⚡", priority: 2, description: "High business value, phase-2 ok" },
  "Could Have":  { color: T.steel,  icon: "💡", priority: 3, description: "Nice to have, defer if needed" },
  "Won't Have":  { color: T.text3,  icon: "🚫", priority: 4, description: "Out of scope" },
};

export default function InterfaceIntelligenceTab() {
  const [view, setView] = useState("criticality");
  const [hoveredId, setHoveredId] = useState(null);
  const nav = useNavigation();

  // Aggregate stats
  const stats = useMemo(() => {
    let totalCrit = 0, totalHours = 0;
    const byMoscow = { "Must Have": 0, "Should Have": 0, "Could Have": 0, "Won't Have": 0 };
    const byOutcomeCat = {};
    for (const f of IFACES) {
      const e = f.enhanced || {};
      totalCrit += e.criticalityScore || 0;
      totalHours += e.taskHours || 0;
      if (e.moscow) byMoscow[e.moscow] = (byMoscow[e.moscow] || 0) + 1;
      if (f.outcomeCat) byOutcomeCat[f.outcomeCat] = (byOutcomeCat[f.outcomeCat] || 0) + 1;
    }
    return {
      avgCrit: totalCrit / IFACES.length,
      totalHours,
      byMoscow,
      byOutcomeCat,
      maxCrit: Math.max(...IFACES.map(f => f.enhanced?.criticalityScore || 0)),
    };
  }, []);

  return (
    <div style={{
      padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto",
      fontFamily: FONTS.body,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🧠 Interface Intelligence
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          Every one of the {IFACES.length} interfaces — beyond what they do, into <em>how critical</em>,
          <em> what business outcome</em>, <em>how reusable</em>, and <em>what depends on what</em>.
        </div>
        <div style={{ fontSize: 9.5, color: T.amber, marginTop: 6, fontStyle: "italic" }}>
          "If you can't explain why each integration matters — in business outcome terms — you'll lose them in the first 5 minutes of a steering committee."
        </div>
      </div>

      {/* Stat strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        {[
          { l: "Interfaces",        v: IFACES.length,                    c: T.cyan },
          { l: "Avg criticality",   v: stats.avgCrit.toFixed(1),         c: T.amber },
          { l: "Total task hours",  v: stats.totalHours,                 c: T.purple },
          { l: "MUST-HAVE",          v: stats.byMoscow["Must Have"] || 0, c: T.red },
          { l: "SHOULD-HAVE",        v: stats.byMoscow["Should Have"] || 0, c: T.amber },
          { l: "Outcome cats",      v: Object.keys(stats.byOutcomeCat).length, c: T.green },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 6,
            padding: "10px 16px", textAlign: "center", minWidth: 96,
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 7.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase", marginTop: 2 }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* View nav */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={chipBtn(view === v.id, v.color)}>
            {v.label}
          </button>
        ))}
      </div>

      {view === "criticality" && <CriticalityView stats={stats} hoveredId={hoveredId} setHoveredId={setHoveredId} nav={nav}/>}
      {view === "moscow"      && <MoscowView byMoscow={stats.byMoscow} nav={nav}/>}
      {view === "deps"        && <DependencyView nav={nav}/>}
      {view === "outcome"     && <OutcomeView byOutcomeCat={stats.byOutcomeCat} nav={nav}/>}
      {view === "cards"       && <CardGridView nav={nav}/>}
    </div>
  );
}

// ─── 1 · Criticality bubble + ranked list ────────────────────────────────
function CriticalityView({ stats, hoveredId, setHoveredId, nav }) {
  const top15 = getInterfacesByCriticality(0).slice(0, 15);
  const W = 1100, H = 380;

  return (
    <div>
      <div style={{
        padding: "10px 14px", marginBottom: 14,
        background: `${T.cyan}10`, border: `1px solid ${T.cyan}30`, borderRadius: 6,
        fontSize: 10, color: T.text2,
      }}>
        💡 Criticality score = composite of risk × complexity × downstream-fan-out × business-impact.
        The Y-axis here is the score; the X-axis is the implementation phase. Larger bubbles = more downstream consumers.
      </div>

      <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
          <defs>
            <filter id="crit-glow">
              <feGaussianBlur stdDeviation="2" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>

          {/* Axes */}
          <line x1={70} y1={H - 40} x2={W - 30} y2={H - 40} stroke={T.text3} strokeWidth="1"/>
          <line x1={70} y1={20}     x2={70}     y2={H - 40} stroke={T.text3} strokeWidth="1"/>

          {/* Y-axis label */}
          <text x={20} y={H/2} fontSize="9" fill={T.text2} fontFamily={FONTS.mono}
            transform={`rotate(-90, 20, ${H/2})`} textAnchor="middle">CRITICALITY SCORE →</text>
          {/* X-axis label */}
          <text x={W/2} y={H - 8} fontSize="9" fill={T.text2} fontFamily={FONTS.mono} textAnchor="middle">
            PHASE →
          </text>

          {/* Gridlines for criticality */}
          {[0, 5, 10, 15, 20, 25].map(v => {
            const y = (H - 40) - (v / 25) * (H - 60);
            return (
              <g key={v}>
                <line x1={70} y1={y} x2={W - 30} y2={y} stroke={T.text3} strokeWidth="0.5" opacity="0.3"/>
                <text x={62} y={y + 3} fontSize="8" fill={T.text3} textAnchor="end" fontFamily={FONTS.mono}>{v}</text>
              </g>
            );
          })}

          {/* Phase labels */}
          {[1, 2, 3, 4, 5, 6, 7, 8].map(p => {
            const x = 70 + (p - 0.5) / 8 * (W - 100);
            return (
              <text key={p} x={x} y={H - 22} fontSize="9" fill={T.text2} textAnchor="middle" fontFamily={FONTS.mono}>
                P{p}
              </text>
            );
          })}

          {/* Bubbles */}
          {IFACES.map(f => {
            const e = f.enhanced || {};
            const score = e.criticalityScore || 0;
            const downstream = (e.downstreamConsumers || []).length;
            const cx = 70 + (f.phase - 0.5) / 8 * (W - 100) + (parseInt(f.id.slice(3)) % 5) * 6 - 12;
            const cy = (H - 40) - (Math.min(score, 25) / 25) * (H - 60);
            const r = 4 + Math.min(downstream, 10) * 1.2;
            const c = GRP_META[f.grp]?.color || T.steel;
            const isHovered = hoveredId === f.id;

            return (
              <g key={f.id}>
                <circle cx={cx} cy={cy} r={r}
                  fill={c}
                  fillOpacity={isHovered ? 0.9 : 0.55}
                  stroke={c}
                  strokeWidth={isHovered ? 2.5 : 1}
                  filter={isHovered ? "url(#crit-glow)" : undefined}
                  style={{ cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={() => setHoveredId(f.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => nav?.goToInterface(f.id)}
                />
                {isHovered && (
                  <g transform={`translate(${cx + r + 6}, ${cy - 24})`} style={{ pointerEvents: "none" }}>
                    <rect x={0} y={0} width={210} height={50} rx={4}
                      fill="#0c1c30ee" stroke={c} strokeWidth="1.5"/>
                    <text x={8} y={14} fontSize="9" fill={c} fontWeight="700" fontFamily={FONTS.mono}>{f.id}</text>
                    <text x={8} y={28} fontSize="9" fill={T.text}>{f.name.slice(0, 28)}</text>
                    <text x={8} y={42} fontSize="8" fill={T.text2}>
                      crit {score} · ⬇ {downstream} consumers · {e.taskHours || 0}h
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Ranked list of top 15 */}
      <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px" }}>
        <div style={{ fontSize: 10, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>
          🌟 TOP 15 MOST CRITICAL INTERFACES
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 8 }}>
          {top15.map((f, idx) => {
            const e = f.enhanced || {};
            const c = GRP_META[f.grp]?.color || T.steel;
            return (
              <div key={f.id}
                onClick={() => nav?.goToInterface(f.id)}
                style={{
                  background: T.bg3, border: `1px solid ${c}40`, borderLeft: `3px solid ${c}`,
                  borderRadius: 4, padding: "8px 10px", cursor: "pointer",
                  display: "flex", gap: 8, alignItems: "center",
                }}>
                <div style={{
                  fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: c, opacity: 0.45,
                  minWidth: 26, textAlign: "center",
                }}>{idx + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: c, fontWeight: 700 }}>{f.id}</span>
                    <span style={pill(T.amber)}>crit {e.criticalityScore}</span>
                  </div>
                  <div style={{ fontSize: 10, color: T.text, marginBottom: 2,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.name}
                  </div>
                  <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono }}>
                    {f.grp} · {(e.downstreamConsumers || []).length}↓ · {e.taskHours || 0}h
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── 2 · MoSCoW matrix ───────────────────────────────────────────────────
function MoscowView({ byMoscow, nav }) {
  return (
    <div>
      <div style={{ padding: "10px 14px", marginBottom: 14,
        background: `${T.pink}10`, border: `1px solid ${T.pink}30`, borderRadius: 6,
        fontSize: 10, color: T.text2 }}>
        💡 MoSCoW = Must / Should / Could / Won't. Maps directly to release planning. Must-haves block go-live; Should-haves go in phase-2; Could-haves are stretch.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 12 }}>
        {Object.entries(MOSCOW_META).map(([m, meta]) => {
          const list = getInterfacesByMoscow(m);
          return (
            <div key={m} style={{
              background: T.bg2,
              border: `1px solid ${meta.color}40`,
              borderTop: `4px solid ${meta.color}`,
              borderRadius: 8, padding: "14px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 28 }}>{meta.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: meta.color }}>
                    {m}
                  </div>
                  <div style={{ fontSize: 9, color: T.text2 }}>{meta.description}</div>
                </div>
                <div style={{
                  fontFamily: FONTS.header, fontSize: 28, fontWeight: 700,
                  color: meta.color, opacity: 0.6,
                }}>{list.length}</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(74px, 1fr))", gap: 4 }}>
                {list.map(f => {
                  const c = GRP_META[f.grp]?.color || T.steel;
                  return (
                    <button key={f.id} onClick={() => nav?.goToInterface(f.id)}
                      title={f.name}
                      style={{
                        background: `${c}15`, border: `1px solid ${c}40`,
                        color: c, padding: "3px 6px",
                        borderRadius: 3, fontSize: 8.5, fontFamily: FONTS.mono,
                        cursor: "pointer",
                      }}>
                      {f.id}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 3 · Dependency graph ────────────────────────────────────────────────
function DependencyView({ nav }) {
  // Sort interfaces by downstream fan-out
  const sorted = useMemo(() => {
    return [...IFACES].map(f => ({
      ...f,
      downstreamCount: (f.enhanced?.downstreamConsumers || []).length,
      upstreamCount: (f.enhanced?.upstreamDependencies || []).length,
    })).sort((a, b) => b.downstreamCount - a.downstreamCount).slice(0, 20);
  }, []);

  return (
    <div>
      <div style={{ padding: "10px 14px", marginBottom: 14,
        background: `${T.amber}10`, border: `1px solid ${T.amber}30`, borderRadius: 6,
        fontSize: 10, color: T.text2 }}>
        💡 Top 20 interfaces by downstream fan-out. If an interface has many downstream consumers, breaking it cascades widely — these are your "load-bearing walls".
      </div>

      <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px" }}>
        {sorted.map(f => {
          const c = GRP_META[f.grp]?.color || T.steel;
          return (
            <div key={f.id} style={{
              display: "grid", gridTemplateColumns: "200px 1fr 200px", gap: 14,
              alignItems: "center", padding: "8px 0",
              borderBottom: `1px solid ${T.border2}`,
            }}>
              {/* Upstream */}
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono, marginBottom: 3 }}>
                  ↑ UPSTREAM ({f.upstreamCount})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "flex-end" }}>
                  {(f.enhanced?.upstreamDependencies || []).slice(0, 4).map(uid => (
                    <span key={uid} onClick={() => nav?.goToInterface(uid)}
                      style={{
                        background: `${T.steel}15`, color: T.steel,
                        border: `1px solid ${T.steel}40`,
                        padding: "1px 5px", fontSize: 8, fontFamily: FONTS.mono,
                        borderRadius: 3, cursor: "pointer",
                      }}>{uid}</span>
                  ))}
                  {(f.enhanced?.upstreamDependencies || []).length > 4 && (
                    <span style={{ fontSize: 8, color: T.text3 }}>+{(f.enhanced?.upstreamDependencies || []).length - 4}</span>
                  )}
                </div>
              </div>

              {/* Centre node */}
              <div onClick={() => nav?.goToInterface(f.id)}
                style={{
                  background: `${c}15`, border: `1px solid ${c}`,
                  padding: "8px 12px", borderRadius: 6, cursor: "pointer",
                  textAlign: "center",
                }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: c, fontWeight: 700 }}>{f.id}</div>
                <div style={{ fontSize: 10, color: T.text, marginTop: 1 }}>{f.name}</div>
              </div>

              {/* Downstream */}
              <div>
                <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono, marginBottom: 3 }}>
                  ↓ DOWNSTREAM ({f.downstreamCount})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {(f.enhanced?.downstreamConsumers || []).slice(0, 6).map(did => (
                    <span key={did} onClick={() => nav?.goToInterface(did)}
                      style={{
                        background: `${c}15`, color: c,
                        border: `1px solid ${c}40`,
                        padding: "1px 5px", fontSize: 8, fontFamily: FONTS.mono,
                        borderRadius: 3, cursor: "pointer",
                      }}>{did}</span>
                  ))}
                  {(f.enhanced?.downstreamConsumers || []).length > 6 && (
                    <span style={{ fontSize: 8, color: T.text3 }}>+{(f.enhanced?.downstreamConsumers || []).length - 6}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 4 · Outcome map ─────────────────────────────────────────────────────
function OutcomeView({ byOutcomeCat, nav }) {
  const cats = getAllOutcomeCategories();
  // Color palette for outcome categories
  const palette = [T.cyan, T.amber, T.green, T.purple, T.pink, T.blue, T.gold, T.orange, T.teal, T.steel];

  return (
    <div>
      <div style={{ padding: "10px 14px", marginBottom: 14,
        background: `${T.green}10`, border: `1px solid ${T.green}30`, borderRadius: 6,
        fontSize: 10, color: T.text2 }}>
        💡 Every interface delivers a concrete business outcome. Grouping them by outcome category proves to stakeholders the integration plan is <em>outcome-driven</em>, not technology-driven.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 12 }}>
        {cats.map((cat, i) => {
          const c = palette[i % palette.length];
          const list = getInterfacesByOutcomeCategory(cat);
          return (
            <div key={cat} style={{
              background: T.bg2,
              border: `1px solid ${c}40`,
              borderLeft: `4px solid ${c}`,
              borderRadius: 6, padding: "12px 14px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: c }}>
                  {cat}
                </span>
                <span style={{
                  fontFamily: FONTS.mono, fontSize: 18, fontWeight: 700, color: c, opacity: 0.6,
                }}>{list.length}</span>
              </div>
              {list.map(f => (
                <div key={f.id} onClick={() => nav?.goToInterface(f.id)}
                  style={{
                    display: "flex", gap: 8, alignItems: "center", padding: "4px 0",
                    cursor: "pointer", borderBottom: `1px dashed ${T.border2}`,
                  }}>
                  <span style={{
                    fontSize: 8, fontFamily: FONTS.mono, color: c,
                    minWidth: 50, fontWeight: 700,
                  }}>{f.id}</span>
                  <span style={{ fontSize: 9, color: T.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.name}
                  </span>
                  <span style={{ fontSize: 8, color: T.amber, fontFamily: FONTS.mono }}>
                    🎯 {f.outcome}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 5 · Card grid — every interface as a rich card ─────────────────────
function CardGridView({ nav }) {
  const [grpFilter, setGrpFilter]   = useState("ALL");
  const [phaseFilter, setPhaseFilter] = useState("ALL");
  const [search, setSearch]         = useState("");

  const filtered = useMemo(() => {
    return IFACES.filter(f => {
      if (grpFilter !== "ALL" && f.grp !== grpFilter) return false;
      if (phaseFilter !== "ALL" && String(f.phase) !== phaseFilter) return false;
      if (search) {
        const hay = `${f.id} ${f.name} ${(f.tags||[]).join(" ")}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [grpFilter, phaseFilter, search]);

  return (
    <div>
      {/* Filter bar */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center",
        padding: "10px 14px", background: T.bg2, borderRadius: 6, border: `1px solid ${T.border}`,
      }}>
        <input type="text" placeholder="🔍 Search ID, name, tags..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            background: T.bg3, border: `1px solid ${T.border}`, color: T.text,
            padding: "5px 10px", borderRadius: 4, fontSize: 10, outline: "none", minWidth: 240,
          }} />
        <select value={grpFilter} onChange={e => setGrpFilter(e.target.value)}
          style={{ background: T.bg3, border: `1px solid ${T.border}`, color: T.text, padding: "5px 8px", borderRadius: 4, fontSize: 10 }}>
          <option value="ALL">All groups</option>
          {Object.entries(GRP_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)}
          style={{ background: T.bg3, border: `1px solid ${T.border}`, color: T.text, padding: "5px 8px", borderRadius: 4, fontSize: 10 }}>
          <option value="ALL">All phases</option>
          {Object.entries(PHASE_META).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
        <span style={{ marginLeft: "auto", fontSize: 10, color: T.text2 }}>
          {filtered.length} of {IFACES.length} shown
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 10 }}>
        {filtered.map(f => <IntelligenceCard key={f.id} iface={f} nav={nav}/>)}
      </div>
    </div>
  );
}

function IntelligenceCard({ iface, nav }) {
  const e = iface.enhanced || {};
  const c = GRP_META[iface.grp]?.color || T.steel;
  const mvp = MVP_META[iface.mvp];

  return (
    <div onClick={() => nav?.goToInterface(iface.id)}
      style={{
        background: T.bg2,
        border: `1px solid ${c}40`,
        borderTop: `3px solid ${c}`,
        borderRadius: 8, padding: "12px 14px", cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 6px 14px ${c}25`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: c, fontWeight: 700 }}>{iface.id}</span>
        <span style={pill(mvp?.c || T.steel)}>MVP-{iface.mvp}</span>
        <span style={pill(T.amber)}>crit {e.criticalityScore || 0}</span>
        <span style={{ marginLeft: "auto", fontSize: 8, color: T.text3, fontFamily: FONTS.mono }}>P{iface.phase}</span>
      </div>

      <div style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 4, lineHeight: 1.3 }}>
        {iface.name}
      </div>

      <div style={{ fontSize: 9, color: c, fontFamily: FONTS.mono, marginBottom: 6 }}>
        {iface.from} → {iface.to}
      </div>

      {e.meaning && (
        <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, marginBottom: 8, fontStyle: "italic" }}>
          {e.meaning.slice(0, 130)}{e.meaning.length > 130 ? "…" : ""}
        </div>
      )}

      {/* Outcome chip */}
      <div style={{
        background: `${T.green}10`, border: `1px solid ${T.green}30`,
        borderRadius: 4, padding: "6px 8px", marginBottom: 8,
      }}>
        <div style={{ fontSize: 7.5, color: T.green, fontWeight: 700, letterSpacing: 1, marginBottom: 2 }}>
          🎯 BUSINESS OUTCOME
        </div>
        <div style={{ fontSize: 10, color: T.text }}>
          {iface.outcome} <span style={{ color: T.text3, fontSize: 8 }}>· {iface.outcomeCat}</span>
        </div>
      </div>

      {/* Key intelligence row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
        {e.moscow && (
          <div style={{ background: T.bg3, padding: "5px 7px", borderRadius: 3 }}>
            <div style={{ fontSize: 7, color: T.text3, letterSpacing: 1 }}>MOSCOW</div>
            <div style={{ fontSize: 9, color: MOSCOW_META[e.moscow]?.color, fontWeight: 700 }}>{e.moscow}</div>
          </div>
        )}
        {e.taskHours !== undefined && (
          <div style={{ background: T.bg3, padding: "5px 7px", borderRadius: 3 }}>
            <div style={{ fontSize: 7, color: T.text3, letterSpacing: 1 }}>TASK HOURS</div>
            <div style={{ fontSize: 9, color: T.amber, fontWeight: 700 }}>{e.taskHours}h</div>
          </div>
        )}
      </div>

      {/* Classification breakdown */}
      {e.classificationSummary && (
        <div style={{ background: T.bg3, padding: "6px 8px", borderRadius: 3, marginBottom: 6 }}>
          <div style={{ fontSize: 7, color: T.text3, letterSpacing: 1, marginBottom: 3 }}>TASK CLASSIFICATION</div>
          <div style={{ display: "flex", gap: 4, height: 6, marginBottom: 3 }}>
            <div style={{ flex: e.classificationSummary.ootb, background: T.green, borderRadius: 1 }}/>
            <div style={{ flex: e.classificationSummary.ootbConfig, background: T.cyan, borderRadius: 1 }}/>
            <div style={{ flex: e.classificationSummary.customization, background: T.red, borderRadius: 1 }}/>
          </div>
          <div style={{ display: "flex", gap: 6, fontSize: 7.5, fontFamily: FONTS.mono }}>
            <span style={{ color: T.green }}>OOTB {e.classificationSummary.ootb}</span>
            <span style={{ color: T.cyan }}>+Config {e.classificationSummary.ootbConfig}</span>
            <span style={{ color: T.red }}>Custom {e.classificationSummary.customization}</span>
          </div>
        </div>
      )}

      {/* Tags */}
      {iface.tags && iface.tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {iface.tags.slice(0, 5).map(t => (
            <span key={t} style={{
              fontSize: 7.5, padding: "1px 5px",
              background: `${c}10`, color: c, border: `1px solid ${c}30`,
              borderRadius: 8, fontFamily: FONTS.mono,
            }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}
